/**
 * Suppliers, and the bank detail control.
 *
 * ═══════════════════════════════════════════════════════════════════
 * WHY BANK DETAILS ARE TREATED DIFFERENTLY FROM EVERY OTHER FIELD.
 *
 * Invoice redirection fraud — a forged letter or email asking for the payment
 * account to be changed — is the most common way a UK organisation loses a
 * large sum at once. So bank details are not an editable field here.
 *
 * Each set of details is an immutable VERSION. Changing them creates a new
 * version which starts as "pending verification" and CANNOT be paid to. A
 * different person must verify it, and must record HOW: a callback to a number
 * already held on file, never a number printed on the request itself.
 *
 * While a change is pending, every payment to that supplier is blocked.
 *
 * The checker has two answers available, not one. If the callback confirms the
 * change they verify it; if it does not, they REJECT it, and the reason they
 * give becomes part of the audit record. A screen that only offers "approve"
 * is not a control.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { lower, useTerms } from '../lib/tenant';
import { date, dateTime, money, statusLabel, statusTone } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  Select,
  TextArea,
} from '../components/ui';

interface SupplierRow {
  id: string;
  code: string;
  name: string;
  status: string;
  supplier_type: string;
  categories: string[] | string;
  payment_terms_days: number;
  payment_method: string;
  vat_number: string | null;
  companies_house_number: string | null;
  balance_minor: number;
  has_bank_details: boolean;
  bank_change_pending: boolean;
  contract_reference: string | null;
  contract_end_date: string | null;
  last_transaction_date: string | null;
  exclude_from_payment_run: boolean;
}

/* ------------------------------------------------------------------ *
 * Contract references
 * ------------------------------------------------------------------ */

interface ContractRow {
  id: string;
  reference: string;
}

/**
 * A supplier record carries the contract REFERENCE but not the contract's id,
 * so the register is read once and the references matched against it. Two
 * things follow from that, both deliberate:
 *
 *  - a user without access to the contract register never sees a link, because
 *    a reference that leads to a page they cannot open is worse than plain
 *    text;
 *  - if the register cannot be read the references simply stay as text. The
 *    supplier screen's own data is unaffected, so a failure here must not put
 *    an error in front of the reader.
 */
function useContractLinks(): Map<string, string> {
  const { can } = useSession();
  const allowed = can('contract.view');
  const register = useAsync<{ contracts: ContractRow[] }>(
    () =>
      allowed
        ? api.get<{ contracts: ContractRow[] }>('/platform/contracts').catch(() => ({ contracts: [] }))
        : Promise.resolve({ contracts: [] }),
    [allowed],
  );
  return useMemo(
    () => new Map((register.data?.contracts ?? []).map((c) => [c.reference, c.id] as const)),
    [register.data],
  );
}

function ContractReference({ reference, contractId }: { reference: string | null; contractId: string | undefined }) {
  if (!reference) return <span className="muted smaller">—</span>;
  if (!contractId) return <span className="mono smaller">{reference}</span>;
  return (
    <Link to={`/contracts/${contractId}`}>
      <span className="mono smaller">{reference}</span>
    </Link>
  );
}

export function SuppliersPage() {
  const t = useTerms();
  const [search, setSearch] = useState('');
  const contractIds = useContractLinks();
  const list = useAsync(
    () => api.get<{ suppliers: SupplierRow[] }>(`/reference/suppliers${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    [search],
  );

  return (
    <>
      <PageHeader
        title={t.supplierPlural}
        subtitle={`The ${lower(t.supplier)} master file, with the bank detail control that protects payments.`}
      />

      <AsyncPanel state={list}>
        {(data) => {
          const pending = data.suppliers.filter((s) => s.bank_change_pending);
          const owed = data.suppliers.reduce((sum, s) => sum + s.balance_minor, 0);
          return (
            <div className="stack">
              {pending.length > 0 ? (
                <Alert tone="warn" title={`${pending.length} bank detail change${pending.length === 1 ? '' : 's'} awaiting verification`}>
                  <p>
                    Payments to{' '}
                    {pending.map((s, i) => (
                      <span key={s.id}>
                        {i > 0 ? ', ' : ''}
                        <Link to={`/suppliers/${s.id}`}>{s.name}</Link>
                      </span>
                    ))}{' '}
                    are blocked until somebody other than the proposer verifies the change.
                  </p>
                </Alert>
              ) : null}

              <Card
                title={t.supplierPlural}
                flush
                actions={
                  <>
                    <label className="row" style={{ gap: '0.4rem' }}>
                      <span className="visually-hidden">Search {lower(t.supplierPlural)}</span>
                      <input
                        type="search"
                        placeholder="Name or code"
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                      />
                    </label>
                    {/* The results stay on screen while a new search runs, so
                        without this the reader has no sign anything happened.
                        It sits outside the label so it never becomes part of
                        the search box's accessible name. */}
                    <span className="smaller muted" role="status" aria-live="polite">
                      {list.loading ? 'Searching…' : ''}
                    </span>
                  </>
                }
              >
                <DataTable
                  caption={`${t.supplierPlural} available to this ${lower(t.entity)}`}
                  rows={data.suppliers}
                  rowKey={(s) => s.id}
                  emptyMessage={
                    search
                      ? `No ${lower(t.supplierPlural)} match “${search}”.`
                      : `No ${lower(t.supplierPlural)} on the master file yet.`
                  }
                  initialSort={{ key: 'name', direction: 'asc' }}
                  footer={
                    <tr className="is-total">
                      <td colSpan={4}>Total owed</td>
                      <td className="num">{money(owed)}</td>
                      <td colSpan={2} />
                    </tr>
                  }
                  columns={[
                    {
                      key: 'code',
                      header: 'Code',
                      width: '7rem',
                      sortValue: (s) => s.code,
                      render: (s) => (
                        <Link to={`/suppliers/${s.id}`}>
                          <span className="mono">{s.code}</span>
                        </Link>
                      ),
                    },
                    {
                      key: 'name',
                      header: 'Name',
                      sortValue: (s) => s.name,
                      render: (s) => (
                        <>
                          <div className="truncate">{s.name}</div>
                          <div className="smaller muted">
                            {Array.isArray(s.categories) ? s.categories.join(', ') : s.categories}
                          </div>
                        </>
                      ),
                    },
                    {
                      key: 'terms',
                      header: 'Terms',
                      sortValue: (s) => s.payment_terms_days,
                      render: (s) => (
                        <span className="smaller">
                          {s.payment_terms_days} days · {statusLabel(s.payment_method)}
                        </span>
                      ),
                    },
                    {
                      key: 'contract',
                      header: 'Contract',
                      render: (s) => (
                        <>
                          <ContractReference
                            reference={s.contract_reference}
                            contractId={s.contract_reference ? contractIds.get(s.contract_reference) : undefined}
                          />
                          {s.contract_reference && s.contract_end_date ? (
                            <div className="smaller muted">to {date(s.contract_end_date)}</div>
                          ) : null}
                        </>
                      ),
                    },
                    {
                      key: 'balance',
                      header: 'Owed',
                      numeric: true,
                      sortValue: (s) => s.balance_minor,
                      render: (s) => <Money minor={s.balance_minor} blankIfZero />,
                    },
                    {
                      key: 'bank',
                      header: 'Bank details',
                      sortValue: (s) => (s.bank_change_pending ? 0 : s.has_bank_details ? 2 : 1),
                      render: (s) =>
                        s.bank_change_pending ? (
                          <Badge tone="error">Change pending</Badge>
                        ) : s.has_bank_details ? (
                          <Badge tone="ok">Verified</Badge>
                        ) : (
                          <Badge tone="warn">None held</Badge>
                        ),
                    },
                    {
                      key: 'status',
                      header: 'Status',
                      sortValue: (s) => s.status,
                      render: (s) => (
                        <>
                          <Badge tone={statusTone(s.status)}>{statusLabel(s.status)}</Badge>
                          {/* A supplier paid by direct debit is deliberately left out
                              of the BACS run; saying so here stops it reading as an
                              omission. */}
                          {s.exclude_from_payment_run ? (
                            <>
                              {' '}
                              <Badge tone="warn">Not in payment runs</Badge>
                            </>
                          ) : null}
                        </>
                      ),
                    },
                  ]}
                />
              </Card>
            </div>
          );
        }}
      </AsyncPanel>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Supplier detail
 * ------------------------------------------------------------------ */

/**
 * Order and invoice totals are BIGINT columns. node-postgres hands those back
 * as strings so that a value beyond 2^53 cannot be silently rounded, while the
 * in-process build returns numbers — hence the widened type and the Number()
 * at every render site. Nothing is divided here; the figure stays in pence.
 */
type MinorFromDb = number | string;

interface BankAccountVersion {
  id: string;
  version_no: number;
  account_name: string;
  sort_code: string;
  /** Masked to the last four digits unless the caller may see it in full. */
  account_number: string;
  status: string;
  proposed_at: string | null;
  verified_at: string | null;
  verification_method: string | null;
  proposal_reason: string | null;
  verification_notes: string | null;
  rejected_reason: string | null;
  proposed_by_name: string | null;
  verified_by_name: string | null;
}

interface SupplierDetail {
  supplier: Record<string, unknown> & {
    id: string;
    code: string;
    name: string;
    status: string;
    hold_reason: string | null;
    supplier_type: string;
    vat_number: string | null;
    companies_house_number: string | null;
    payment_terms_days: number;
    payment_method: string;
    contract_reference: string | null;
    contract_start_date: string | null;
    contract_end_date: string | null;
    contract_notice_period_days: number | null;
    contract_auto_renews: boolean;
    is_preferred?: boolean;
    preferred_reason?: string | null;
    centrally_approved?: boolean;
    shared_across_entities?: boolean;
    exclude_from_payment_run: boolean;
  };
  addresses: Array<{
    id: string;
    line1: string;
    line2: string | null;
    town: string;
    county: string | null;
    postcode: string;
    address_type: string;
  }>;
  contacts: Array<{ id: string; name: string; email: string | null; telephone: string | null; contact_type: string }>;
  bankAccounts: BankAccountVersion[];
  notes: Array<{ id: string; body: string; created_at: string; created_by_name: string | null }>;
  recentOrders: Array<{ id: string; order_no: string; order_date: string; gross_total_minor: MinorFromDb; status: string }>;
  recentInvoices: Array<{
    id: string;
    invoice_no: string;
    invoice_date: string;
    gross_total_minor: MinorFromDb;
    outstanding_minor: MinorFromDb;
    status: string;
  }>;
  spendByYear: Array<{ yearCode: string; netMinor: number; invoiceCount: number }>;
  /** False when this reader sees account numbers masked. */
  canViewFullBankDetails: boolean;
}

/**
 * The labels are ours; the values are the server's enum, and must stay in step
 * with it — a method the API will not accept is a dialog that fails on submit.
 */
const VERIFICATION_METHODS = [
  { value: 'callback_to_known_number', label: 'Telephone callback to a number already held on file', short: 'Telephone callback to a known number' },
  { value: 'in_person', label: 'In person with a known contact', short: 'In person with a known contact' },
  { value: 'bank_letter_verified', label: 'Bank letter verified with the bank', short: 'Bank letter verified' },
  { value: 'existing_contract', label: 'Checked against the signed contract already on file', short: 'Checked against the signed contract' },
  { value: 'other', label: 'Another method — described below', short: 'Another method' },
];

/**
 * The history reads back in the same words the checker was offered, rather than
 * an unpicked enum. Anything the server holds that is not in the list above
 * still gets humanised rather than shown raw.
 */
function verificationMethodLabel(value: string): string {
  return VERIFICATION_METHODS.find((m) => m.value === value)?.short ?? statusLabel(value);
}

interface DecisionResult {
  supplierName: string;
  supplierCode: string;
  status: string;
}

export function SupplierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can, user } = useSession();
  const t = useTerms();
  const contractIds = useContractLinks();
  const detail = useAsync(() => api.get<SupplierDetail>(`/reference/suppliers/${id}`), [id]);

  const [checking, setChecking] = useState(false);
  const [method, setMethod] = useState(VERIFICATION_METHODS[0]!.value);
  const [notes, setNotes] = useState('');
  const [inFlight, setInFlight] = useState<'verify' | 'reject' | null>(null);
  const [outcome, setOutcome] = useState<DecisionResult | null>(null);

  // Moving to another supplier must not carry the previous one's confirmation
  // or a half-finished dialog with it.
  useEffect(() => {
    setChecking(false);
    setNotes('');
    setInFlight(null);
    setOutcome(null);
  }, [id]);

  const decide = useMutation((input: { bankAccountId: string; decision: 'verify' | 'reject' }) =>
    api.post<DecisionResult>(
      `/self-service/bank-details/${input.bankAccountId}/verify`,
      // The endpoint takes `method` and `notes`, and only asks for a method
      // when the change is being accepted — a rejection is recorded by its
      // reason alone.
      input.decision === 'verify'
        ? { decision: 'verify', method, notes }
        : { decision: 'reject', notes },
    ),
  );

  const evidenceRecorded = notes.trim().length >= 10;

  return (
    <AsyncPanel state={detail}>
      {(data) => {
        const s = data.supplier;
        const pending = data.bankAccounts.find((b) => b.status === 'pending_verification');
        const active = data.bankAccounts.find((b) => b.status === 'active');
        const contractId = s.contract_reference ? contractIds.get(s.contract_reference) : undefined;

        // Closing the dialog discards the previous attempt's failure with it,
        // so reopening never starts on a stale red panel.
        const closeDialog = () => {
          setChecking(false);
          decide.clearError();
        };

        const submit = (decision: 'verify' | 'reject') => {
          if (!pending) return;
          setInFlight(decision);
          void decide.run({ bankAccountId: pending.id, decision }).then((result) => {
            setInFlight(null);
            // A null result means the server refused; its message is already on
            // screen inside the dialog, so the dialog stays open.
            if (result === null) return;
            setChecking(false);
            setNotes('');
            setOutcome(result);
            detail.reload();
          });
        };

        const details: Array<[ReactNode, ReactNode]> = [
          ['Payment terms', `${s.payment_terms_days} days`],
          ['Payment method', statusLabel(s.payment_method)],
          ['VAT number', s.vat_number ?? '—'],
          ['Company number', s.companies_house_number ?? '—'],
          ['Contract', <ContractReference key="ref" reference={s.contract_reference} contractId={contractId} />],
        ];
        if (s.contract_reference) {
          details.push([
            'Contract period',
            s.contract_start_date
              ? `${date(s.contract_start_date)} to ${s.contract_end_date ? date(s.contract_end_date) : '—'}`
              : '—',
          ]);
          details.push([
            'Notice period',
            s.contract_notice_period_days ? `${s.contract_notice_period_days} days` : '—',
          ]);
          details.push([
            'Renewal',
            s.contract_auto_renews ? 'Renews automatically' : 'Expires — it does not renew automatically',
          ]);
        }
        details.push([
          'Active bank account',
          active ? <span className="mono" key="acct">{active.sort_code} {active.account_number}</span> : 'None held',
        ]);
        if (s.hold_reason) details.push(['Reason for hold', s.hold_reason]);

        return (
          <>
            <PageHeader
              title={s.name}
              subtitle={
                <>
                  <span className="mono">{s.code}</span> · {statusLabel(s.supplier_type)}
                </>
              }
              meta={
                <>
                  <Badge tone={statusTone(s.status)}>{statusLabel(s.status)}</Badge>
                  {s.is_preferred ? <Badge tone="brand">Preferred</Badge> : null}
                  {s.centrally_approved ? <Badge tone="info">Centrally approved</Badge> : null}
                  {s.shared_across_entities ? (
                    <Badge tone="info">Shared across all {lower(t.entityPlural)}</Badge>
                  ) : null}
                  {s.exclude_from_payment_run ? <Badge tone="warn">Excluded from payment runs</Badge> : null}
                </>
              }
            />

            {outcome ? (
              outcome.status === 'rejected' ? (
                <Alert tone="warn" title="The proposed bank details were rejected">
                  <p>
                    The proposed account for {outcome.supplierName} cannot be paid to. The details previously verified
                    remain in force, and your reason has been written to the audit trail.
                  </p>
                </Alert>
              ) : (
                <Alert tone="ok" title="Bank details verified">
                  <p>
                    The new account for {outcome.supplierName} is now the active one, and payments to this{' '}
                    {lower(t.supplier)} are released. Who verified it, how, and what they recorded are all in the audit
                    trail.
                  </p>
                </Alert>
              )
            ) : null}

            {pending ? (
              <Alert tone="error" title="A bank detail change is awaiting verification">
                <p>
                  Every payment to {s.name} is blocked until this is verified by somebody other than{' '}
                  {pending.proposed_by_name ?? 'the proposer'}.
                </p>
                <p className="smaller">
                  <strong>Proposed:</strong> {pending.account_name} · <span className="mono">{pending.sort_code} {pending.account_number}</span>
                  {pending.proposed_at ? <> · {dateTime(pending.proposed_at)}</> : null}
                </p>
                <p className="smaller">
                  <strong>Replacing:</strong>{' '}
                  {active ? (
                    <span className="mono">
                      {active.sort_code} {active.account_number}
                    </span>
                  ) : (
                    'no account previously held'
                  )}
                </p>
                {pending.proposal_reason ? (
                  <p className="smaller muted">Stated reason: {pending.proposal_reason}</p>
                ) : null}
                {can('supplier.bank.verify') && pending.proposed_by_name !== user?.fullName ? (
                  <p style={{ marginTop: '0.75rem' }}>
                    <button type="button" className="btn btn--sm btn--primary" onClick={() => setChecking(true)}>
                      Check this change
                    </button>
                  </p>
                ) : (
                  <p className="smaller">
                    {pending.proposed_by_name === user?.fullName
                      ? 'You proposed this change, so you cannot verify it. That is the control.'
                      : 'You do not hold the permission to verify bank details.'}
                  </p>
                )}
              </Alert>
            ) : null}

            <div className="grid grid--3">
              <div style={{ gridColumn: 'span 2' }} className="stack">
                <Card
                  title="Bank detail history"
                  subtitle="Every version, with who proposed it, who checked it and what they recorded."
                  flush
                  footer={
                    data.canViewFullBankDetails ? undefined : (
                      <p className="smaller muted">
                        Account numbers are shown masked to the last four digits. Seeing them in full is a separate
                        permission, held by the people who prepare payments.
                      </p>
                    )
                  }
                >
                  {data.bankAccounts.length === 0 ? (
                    <div style={{ padding: '1rem' }}>
                      <EmptyState title="No bank details held">
                        <p>
                          Nothing can be paid to this {lower(t.supplier)} until an account is proposed and independently
                          verified.
                        </p>
                      </EmptyState>
                    </div>
                  ) : (
                    <DataTable
                      caption="Bank account versions for this supplier"
                      rows={data.bankAccounts}
                      rowKey={(b) => b.id}
                      columns={[
                        { key: 'v', header: 'Version', width: '5rem', render: (b) => b.version_no },
                        {
                          key: 'account',
                          header: 'Account',
                          render: (b) => (
                            <>
                              <div>{b.account_name}</div>
                              <div className="mono smaller muted">
                                {b.sort_code} {b.account_number}
                              </div>
                            </>
                          ),
                        },
                        {
                          key: 'proposed',
                          header: 'Proposed by',
                          render: (b) => (
                            <>
                              <div className="smaller">{b.proposed_by_name ?? '—'}</div>
                              {/* The gap between proposal and check is the thing an
                                  auditor measures, so both carry a time. */}
                              {b.proposed_at ? <div className="smaller muted">{dateTime(b.proposed_at)}</div> : null}
                            </>
                          ),
                        },
                        {
                          key: 'verified',
                          header: 'Checked by',
                          render: (b) =>
                            b.verified_by_name ? (
                              <>
                                <div className="smaller">{b.verified_by_name}</div>
                                {b.verified_at ? <div className="smaller muted">{dateTime(b.verified_at)}</div> : null}
                                {b.verification_method ? (
                                  <div className="smaller muted">{verificationMethodLabel(b.verification_method)}</div>
                                ) : null}
                                {b.rejected_reason ? (
                                  <div className="smaller muted">{b.rejected_reason}</div>
                                ) : b.verification_notes ? (
                                  <div className="smaller muted">{b.verification_notes}</div>
                                ) : null}
                              </>
                            ) : (
                              <span className="muted smaller">Not yet checked</span>
                            ),
                        },
                        { key: 'status', header: 'Status', render: (b) => <Badge tone={statusTone(b.status)}>{statusLabel(b.status)}</Badge> },
                      ]}
                    />
                  )}
                </Card>

                <Card title="Recent invoices" flush>
                  <DataTable
                    caption={`Invoices from ${s.name}`}
                    rows={data.recentInvoices}
                    rowKey={(i) => i.id}
                    emptyMessage={`No invoices from this ${lower(t.supplier)} yet.`}
                    pageSize={10}
                    columns={[
                      {
                        key: 'no',
                        header: 'Invoice',
                        render: (i) => (
                          <Link to={`/invoices/${i.id}`}>
                            <span className="mono">{i.invoice_no}</span>
                          </Link>
                        ),
                      },
                      { key: 'date', header: 'Dated', sortValue: (i) => i.invoice_date, render: (i) => date(i.invoice_date) },
                      {
                        key: 'value',
                        header: 'Value',
                        numeric: true,
                        sortValue: (i) => Number(i.gross_total_minor),
                        render: (i) => <Money minor={Number(i.gross_total_minor)} />,
                      },
                      {
                        key: 'outstanding',
                        header: 'Outstanding',
                        numeric: true,
                        sortValue: (i) => Number(i.outstanding_minor),
                        render: (i) => <Money minor={Number(i.outstanding_minor)} blankIfZero />,
                      },
                      { key: 'status', header: 'Status', render: (i) => <Badge tone={statusTone(i.status)}>{statusLabel(i.status)}</Badge> },
                    ]}
                  />
                </Card>

                <Card title={`Recent ${lower(t.purchaseOrderPlural)}`} flush>
                  <DataTable
                    caption={`Orders placed with ${s.name}`}
                    rows={data.recentOrders}
                    rowKey={(o) => o.id}
                    emptyMessage={`No ${lower(t.purchaseOrderPlural)} placed with this ${lower(t.supplier)} yet.`}
                    pageSize={10}
                    columns={[
                      {
                        key: 'no',
                        header: 'Order',
                        render: (o) => (
                          <Link to={`/orders/${o.id}`}>
                            <span className="mono">{o.order_no}</span>
                          </Link>
                        ),
                      },
                      { key: 'date', header: 'Ordered', sortValue: (o) => o.order_date, render: (o) => date(o.order_date) },
                      {
                        key: 'value',
                        header: 'Value',
                        numeric: true,
                        sortValue: (o) => Number(o.gross_total_minor),
                        render: (o) => <Money minor={Number(o.gross_total_minor)} />,
                      },
                      { key: 'status', header: 'Status', render: (o) => <Badge tone={statusTone(o.status)}>{statusLabel(o.status)}</Badge> },
                    ]}
                  />
                </Card>
              </div>

              <div className="stack">
                <Card title="Details">
                  <DefinitionList items={details} />
                  {s.preferred_reason ? (
                    <p className="smaller muted" style={{ marginTop: '0.75rem' }}>
                      <strong>Preferred:</strong> {s.preferred_reason}
                    </p>
                  ) : null}
                </Card>

                {data.spendByYear.length > 0 ? (
                  <Card title="Spend" subtitle="Invoiced, net of VAT, by financial year.">
                    <DefinitionList
                      items={data.spendByYear.map((y): [ReactNode, ReactNode] => [
                        y.yearCode,
                        <>
                          <Money minor={y.netMinor} />{' '}
                          <span className="smaller muted">
                            · {y.invoiceCount} invoice{y.invoiceCount === 1 ? '' : 's'}
                          </span>
                        </>,
                      ])}
                    />
                  </Card>
                ) : null}

                {data.contacts.length > 0 ? (
                  <Card title="Contacts">
                    <ul className="stack stack--sm">
                      {data.contacts.map((c) => (
                        <li key={c.id}>
                          <div>
                            {c.name} <span className="smaller muted">· {statusLabel(c.contact_type)}</span>
                          </div>
                          <div className="smaller muted">
                            {[c.email, c.telephone].filter(Boolean).join(' · ')}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ) : null}

                {data.addresses.length > 0 ? (
                  <Card title="Addresses">
                    <ul className="stack stack--sm">
                      {data.addresses.map((a) => (
                        <li key={a.id} className="smaller">
                          {[a.line1, a.line2, a.town, a.county, a.postcode].filter(Boolean).join(', ')}
                          <div className="muted">{statusLabel(a.address_type)}</div>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ) : null}

                {data.notes.length > 0 ? (
                  <Card title="Notes">
                    <ul className="stack stack--sm">
                      {data.notes.map((n) => (
                        <li key={n.id} className="smaller">
                          {n.body}
                          <div className="muted">
                            {n.created_by_name ?? 'Unknown'} · {dateTime(n.created_at)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ) : null}
              </div>
            </div>

            {pending ? (
              <Modal
                open={checking}
                onClose={closeDialog}
                title="Check the proposed bank details"
                footer={
                  <>
                    <button type="button" className="btn" onClick={closeDialog}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={decide.busy || !evidenceRecorded}
                      onClick={() => submit('reject')}
                    >
                      {inFlight === 'reject' ? 'Recording…' : 'Reject this change'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={decide.busy || !evidenceRecorded}
                      onClick={() => submit('verify')}
                    >
                      {inFlight === 'verify' ? 'Recording…' : 'Confirm verification'}
                    </button>
                  </>
                }
              >
                <div className="stack">
                  <Alert tone="warn" title="Before you decide">
                    <p className="smaller">
                      Call the {lower(t.supplier)} on a number you already hold — from the signed contract or a previous
                      invoice — never a number written on the request. A forged request carries a forged telephone
                      number. If the call does not confirm the change, reject it.
                    </p>
                  </Alert>

                  <DefinitionList
                    items={[
                      [
                        'Proposed account',
                        <>
                          <div>{pending.account_name}</div>
                          <div className="mono smaller">
                            {pending.sort_code} {pending.account_number}
                          </div>
                        </>,
                      ],
                      [
                        'Replacing',
                        active ? (
                          <span className="mono">
                            {active.sort_code} {active.account_number}
                          </span>
                        ) : (
                          'No account previously held'
                        ),
                      ],
                      [
                        'Proposed by',
                        `${pending.proposed_by_name ?? 'Unknown'}${pending.proposed_at ? `, ${dateTime(pending.proposed_at)}` : ''}`,
                      ],
                      ['Stated reason', pending.proposal_reason ?? '—'],
                    ]}
                  />

                  <Select
                    label="How did you verify it?"
                    hint="Only needed if you are confirming the change; a rejection is recorded by its reason alone."
                    value={method}
                    onChange={(e) => setMethod(e.currentTarget.value)}
                  >
                    {VERIFICATION_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                  <TextArea
                    label="What did you do?"
                    hint="Who you spoke to, on which number, and when. This is the evidence an auditor reads, so both buttons stay locked until it is written."
                    value={notes}
                    onChange={(e) => setNotes(e.currentTarget.value)}
                    required
                  />
                  <ErrorNotice error={decide.error} />
                </div>
              </Modal>
            ) : null}
          </>
        );
      }}
    </AsyncPanel>
  );
}
